export const PAPER_SIZES = {
    a4: { id: "a4", widthMm: 210, heightMm: 297 },
    a5: { id: "a5", widthMm: 148, heightMm: 210 },
    a6: { id: "a6", widthMm: 105, heightMm: 148 },
    tent: { id: "tent", widthMm: 148, heightMm: 105 },
};

export const POSTER_ICONS = [
    "building",
    "heart",
    "star",
    "shield",
    "sparkle",
    "handshake",
    "leaf",
    "chat",
];

export const SAMPLE_ITEM_NAME = "۹۰۷";

export const DEFAULT_PRINT_POSTER_AI_MODEL = "gpt-5.6-sol";

export const PRINT_POSTER_AI_MODELS = [
    { id: "gpt-5.6-sol", labelKey: "print.poster.ai.model.gpt-sol" },
    { id: "kimi-k3", labelKey: "print.poster.ai.model.kimi" },
    { id: "gemini-3.1-flash-image", labelKey: "print.poster.ai.model.banana2" },
    { id: "gemini-3.7-flash", labelKey: "print.poster.ai.model.gemini-flash" },
    { id: "gpt-5.6-terra", labelKey: "print.poster.ai.model.gpt-terra" },
];

export const POSTER_TONES = [
    "hospitality",
    "luxury",
    "friendly",
    "corporate",
    "minimal",
];

export const POSTER_FONTS = [
    {
        id: "yekan",
        stack: "YekanBakhWeb, Tahoma, sans-serif",
        source: "local",
    },
    {
        id: "iran-sans",
        stack: "IRANSansWeb, Tahoma, sans-serif",
        source: "local",
    },
    {
        id: "vazirmatn",
        stack: '"Vazirmatn", Tahoma, sans-serif',
        source: "google",
    },
    {
        id: "amiri",
        stack: 'Amiri, "Noto Naskh Arabic", serif',
        source: "google",
    },
    {
        id: "markazi",
        stack: '"Markazi Text", "Noto Naskh Arabic", serif',
        source: "google",
    },
    {
        id: "ibm-plex",
        stack: '"IBM Plex Sans Arabic", Tahoma, sans-serif',
        source: "google",
    },
    {
        id: "noto-naskh",
        stack: '"Noto Naskh Arabic", serif',
        source: "google",
    },
    {
        id: "lalezar",
        stack: "Lalezar, Tahoma, sans-serif",
        source: "google",
    },
];

export const POSTER_FONT_MAP = Object.fromEntries(
    POSTER_FONTS.map((font) => [font.id, font])
);

export const POSTER_FOCUS_ELEMENTS = ["qr", "headline", "brand", "balanced"];

export const POSTER_GOOGLE_FONTS_HREF =
    "https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&family=IBM+Plex+Sans+Arabic:wght@400;600;700&family=Lalezar&family=Markazi+Text:wght@400;500;600;700&family=Noto+Naskh+Arabic:wght@400;500;600;700&family=Vazirmatn:wght@400;500;600;700;800&display=swap";

export function posterFontStack(fontId) {
    return POSTER_FONT_MAP[fontId]?.stack || POSTER_FONT_MAP.yekan.stack;
}

export function ensurePosterFontsLoaded() {
    if (typeof document === "undefined") return;
    if (document.getElementById("print-poster-google-fonts")) return;
    const link = document.createElement("link");
    link.id = "print-poster-google-fonts";
    link.rel = "stylesheet";
    link.href = POSTER_GOOGLE_FONTS_HREF;
    document.head.appendChild(link);
}

export const FALLBACK_TEMPLATES = [
    {
        id: "classic-gold",
        paper_size: "a5",
        orientation: "portrait",
        colors: {
            background: "#F6F0E4",
            text: "#1B365D",
            accent: "#C6A15B",
            muted: "#4A5F7A",
        },
    },
    {
        id: "midnight-navy",
        paper_size: "a5",
        orientation: "portrait",
        colors: {
            background: "#10203A",
            text: "#F7F1E3",
            accent: "#D4AF6A",
            muted: "#C9D3E0",
        },
    },
    {
        id: "minimal-white",
        paper_size: "a6",
        orientation: "portrait",
        colors: {
            background: "#FFFFFF",
            text: "#1E293B",
            accent: "#1E3A8A",
            muted: "#64748B",
        },
    },
    {
        id: "hospitality-frame",
        paper_size: "a4",
        orientation: "portrait",
        colors: {
            background: "#FBF7EE",
            text: "#1B365D",
            accent: "#B8860B",
            muted: "#5B6B82",
        },
    },
    {
        id: "corporate-slate",
        paper_size: "a5",
        orientation: "portrait",
        colors: {
            background: "#F8FAFC",
            text: "#0F172A",
            accent: "#1E3A8A",
            muted: "#475569",
        },
    },
    {
        id: "warm-welcome",
        paper_size: "a6",
        orientation: "portrait",
        colors: {
            background: "#F8F1E7",
            text: "#4A2C1A",
            accent: "#C05621",
            muted: "#7C5A43",
        },
    },
];

export function paperBox(poster) {
    const paper = PAPER_SIZES[poster?.paper_size] || PAPER_SIZES.a5;
    const landscape =
        poster?.paper_size === "tent" || poster?.orientation === "landscape";
    const widthMm = landscape ? Math.max(paper.widthMm, paper.heightMm) : paper.widthMm;
    const heightMm = landscape ? Math.min(paper.widthMm, paper.heightMm) : paper.heightMm;
    return {
        widthMm,
        heightMm,
        pageSize: `${widthMm}mm ${heightMm}mm`,
    };
}

function hexToRgb(value, fallback) {
    const normalized = /^#[0-9A-Fa-f]{6}$/.test(value || "") ? value : fallback;
    return {
        r: Number.parseInt(normalized.slice(1, 3), 16),
        g: Number.parseInt(normalized.slice(3, 5), 16),
        b: Number.parseInt(normalized.slice(5, 7), 16),
    };
}

function rgbToHex({ r, g, b }) {
    const channel = (value) =>
        Math.round(value).toString(16).padStart(2, "0").toUpperCase();
    return `#${channel(r)}${channel(g)}${channel(b)}`;
}

function mixHex(first, second, firstWeight) {
    const a = hexToRgb(first, "#000000");
    const b = hexToRgb(second, "#FFFFFF");
    return rgbToHex({
        r: a.r * firstWeight + b.r * (1 - firstWeight),
        g: a.g * firstWeight + b.g * (1 - firstWeight),
        b: a.b * firstWeight + b.b * (1 - firstWeight),
    });
}

function relativeLuminance(value) {
    const { r, g, b } = hexToRgb(value, "#000000");
    const linear = [r, g, b].map((channel) => {
        const normalized = channel / 255;
        return normalized <= 0.03928
            ? normalized / 12.92
            : ((normalized + 0.055) / 1.055) ** 2.4;
    });
    return linear[0] * 0.2126 + linear[1] * 0.7152 + linear[2] * 0.0722;
}

function contrastRatio(first, second) {
    const lighter = Math.max(
        relativeLuminance(first),
        relativeLuminance(second)
    );
    const darker = Math.min(
        relativeLuminance(first),
        relativeLuminance(second)
    );
    return (lighter + 0.05) / (darker + 0.05);
}

function highestContrast(background, candidates) {
    return candidates.reduce((best, candidate) =>
        contrastRatio(candidate, background) >
        contrastRatio(best, background)
            ? candidate
            : best
    );
}

export function posterQrTheme(poster) {
    const colors = poster?.colors || {};
    const background = /^#[0-9A-Fa-f]{6}$/.test(colors.background || "")
        ? colors.background.toUpperCase()
        : "#F6F0E4";
    const text = /^#[0-9A-Fa-f]{6}$/.test(colors.text || "")
        ? colors.text.toUpperCase()
        : "#1B365D";
    const accent = /^#[0-9A-Fa-f]{6}$/.test(colors.accent || "")
        ? colors.accent.toUpperCase()
        : "#C6A15B";
    const isDark = relativeLuminance(background) < 0.16;

    if (isDark) {
        const qrBackground = mixHex(accent, "#FFFFFF", 0.14);
        const darkAccent = mixHex(accent, "#000000", 0.58);
        const preferred =
            contrastRatio(darkAccent, qrBackground) >= 4.5
                ? darkAccent
                : highestContrast(qrBackground, [text, "#111827", "#FFFFFF"]);
        return { foreground: preferred, background: qrBackground };
    }

    const accentInk = mixHex(accent, "#000000", 0.64);
    const foreground =
        contrastRatio(text, background) >= 4.5
            ? text
            : contrastRatio(accentInk, background) >= 4.5
              ? accentInk
              : highestContrast(background, ["#111827", "#FFFFFF"]);
    return { foreground, background };
}

export function posterQrRatio(focus) {
    switch (focus) {
        case "headline":
            return { w: 0.36, h: 0.25 };
        case "brand":
            return { w: 0.4, h: 0.28 };
        case "balanced":
            return { w: 0.44, h: 0.31 };
        default:
            return { w: 0.52, h: 0.37 };
    }
}

export function emptyPoster() {
    return {
        template_id: "classic-gold",
        paper_size: "a5",
        orientation: "portrait",
        brand_name: "",
        brand_subtitle: "",
        affiliation: "",
        headline: "",
        subtitle: "",
        instruction: "",
        values: [],
        footer: "",
        signoff: "",
        badge_label: "",
        show_badge: true,
        show_logo: true,
        font_family: "yekan",
        focus_element: "qr",
        layout: "stack",
        ornament: "frame",
        align: "center",
        qr_size: "medium",
        show_values: true,
        colors: {
            background: "#F6F0E4",
            text: "#1B365D",
            accent: "#C6A15B",
            muted: "#4A5F7A",
        },
        ai_generated: false,
    };
}
